import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AxiosError } from "axios";
import { WizardProvider, useWizard } from "@/features/wizard/context/WizardContext";
import { FormTypeSelection } from "@/features/wizard/components/FormTypeSelection";
import { WizardLayout } from "@/features/wizard/components/WizardLayout";
import { BusinessWizardSteps } from "@/features/wizard/components/BusinessWizardSteps";
import { IndividualWizardSteps } from "@/features/wizard/components/IndividualWizardSteps";
import { FORM_FILING_TYPE } from "@/features/wizard/types/wizard.types";
import type { FormFilingType } from "@/features/wizard/types/wizard.types";
import type { IndividualFormData } from "@/features/wizard/types/individual.types";
import type { BusinessFormData } from "@/features/wizard/types/business.types";
import { validateIndividualStep, validateBusinessStep } from "@/features/wizard/validation/wizardValidation";
import { useFormDraft, getFormById } from "@/features/forms";
import type { FormResponse } from "@/features/forms";
import { useTaxYearStore } from "@/features/tax-year/store/taxYearStore";
import { useTaxYear } from "@/features/tax-year";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const FORM_META_KEYS = new Set([
  "id",
  "_id",
  "filingType",
  "taxYear",
  "formName",
  "userId",
  "tenantId",
  "createdBy",
  "lastModifiedBy",
  "createdAt",
  "updatedAt",
  "referenceId",
]);

function formPayloadFromResponse(form: FormResponse): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(form)) {
    if (!FORM_META_KEYS.has(key) && value !== undefined) {
      out[key] = value;
    }
  }
  return out;
}

function WizardContent() {
  const { formType, formName, currentStepIndex, draftId, businessData, individualData, setDraftId } =
    useWizard();
  const mainRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const { saveDraft } = useFormDraft();
  const effectiveYear = useTaxYearStore((s) => s.getEffectiveYear());
  // Wizard is outside MainLayout; still load tax year config for create/save
  useTaxYear();

  useEffect(() => {
    if (mainRef.current && formType) {
      mainRef.current.focus({ preventScroll: true });
    }
  }, [formType, currentStepIndex]);

  const handleSaveDraft = async () => {
    if (!formType) return;
    if (effectiveYear == null) {
      throw new Error("Tax year is not ready yet");
    }
    const result = await saveDraft({
      formType,
      taxYear: effectiveYear,
      draftId,
      businessData,
      individualData,
      formName,
    });
    if (!result) throw new Error("Save draft failed");
    setDraftId(result.formId);
  };

  const handleSaveAndFinish = async () => {
    await handleSaveDraft();
    navigate("/file-tax", { replace: true });
  };

  if (!formType) {
    return <FormTypeSelection />;
  }

  const stepValidation = (): string | null => {
    if (formType === "INDIVIDUAL") {
      return validateIndividualStep(currentStepIndex, individualData);
    }
    if (formType === "BUSINESS") {
      return validateBusinessStep(currentStepIndex, businessData);
    }
    return null;
  };

  return (
    <WizardLayout
      onSaveDraft={handleSaveDraft}
      onSaveAndFinish={handleSaveAndFinish}
      stepValidation={stepValidation}
    >
      {formType === FORM_FILING_TYPE.BUSINESS && <BusinessWizardSteps />}
      {formType === FORM_FILING_TYPE.INDIVIDUAL && <IndividualWizardSteps />}
    </WizardLayout>
  );
}

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "ready";
      formType: FormFilingType;
      draftId: string;
      formName?: string | null;
      individualData?: IndividualFormData;
      businessData?: BusinessFormData;
    };

export default function TaxFilingWizardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loadState, setLoadState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    if (!id) {
      navigate("/file-tax", { replace: true });
      return;
    }
    let cancelled = false;
    setLoadState({ status: "loading" });
    getFormById(id)
      .then((form) => {
        if (cancelled) return;
        const filingType = form.filingType as FormFilingType | undefined;
        const draftId = (form as { id?: string }).id ?? (form as { _id?: string })._id ?? id;
        if (filingType !== "INDIVIDUAL" && filingType !== "BUSINESS") {
          setLoadState({ status: "error", message: "Unknown form type" });
          return;
        }
        const payload = formPayloadFromResponse(form);
        const formName = (form as { formName?: string }).formName ?? null;
        if (filingType === "INDIVIDUAL") {
          setLoadState({
            status: "ready",
            formType: "INDIVIDUAL",
            draftId,
            individualData: payload as IndividualFormData,
          });
        } else {
          setLoadState({
            status: "ready",
            formType: "BUSINESS",
            draftId,
            formName: formName || null,
            businessData: payload as BusinessFormData,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const message =
            err instanceof AxiosError
              ? (err.response?.data?.message ?? err.response?.data?.error ?? err.message)
              : err instanceof Error
                ? err.message
                : "Failed to load form";
          setLoadState({
            status: "error",
            message: message ?? "Failed to load form",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loadState.status === "loading") {
    return (
      <div className="container mx-auto max-w-2xl px-4 py-12">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (loadState.status === "error") {
    return (
      <div className="container mx-auto max-w-md px-4 py-12 text-center space-y-4">
        <p className="text-destructive">{loadState.message}</p>
        <Button asChild variant="outline">
          <Link to="/file-tax">Back to my forms</Link>
        </Button>
      </div>
    );
  }

  return (
    <WizardProvider
      initialFormType={loadState.formType}
      initialDraftId={loadState.draftId}
      initialFormName={loadState.formName}
      initialIndividualData={loadState.individualData}
      initialBusinessData={loadState.businessData}
    >
      <WizardContent />
    </WizardProvider>
  );
}
